import React from 'react';

const HealthCheckPanel = ({ healthData, loading }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="space-y-3">
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
      case 'active':
        return 'text-green-600 bg-green-100';
      case 'degraded':
      case 'inactive':
        return 'text-yellow-600 bg-yellow-100';
      case 'down':
      case 'error':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
      case 'active':
        return '✓';
      case 'degraded':
      case 'inactive':
        return '⚠';
      case 'down':
      case 'error':
        return '✗';
      default:
        return '?';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-gray-900">System Health</h3>
        <span className="text-sm text-gray-500">
          {healthData?.timestamp ? new Date(healthData.timestamp).toLocaleTimeString() : 'Loading...'}
        </span>
      </div>

      <div className="space-y-4">
        {/* Database Status */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-3">
            <span className={`text-2xl ${getStatusColor(healthData?.database_status)}`}>
              {getStatusIcon(healthData?.database_status)}
            </span>
            <div>
              <div className="font-semibold text-gray-900">Database</div>
              <div className="text-sm text-gray-600">
                {healthData?.database_response_time_ms ?
                  `${healthData.database_response_time_ms.toFixed(0)}ms response time` :
                  'Checking...'}
              </div>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(healthData?.database_status)}`}>
            {healthData?.database_status || 'Unknown'}
          </span>
        </div>

        {/* API Services */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-3">
            <span className={`text-2xl ${getStatusColor(healthData?.api_status)}`}>
              {getStatusIcon(healthData?.api_status)}
            </span>
            <div>
              <div className="font-semibold text-gray-900">API Services</div>
              <div className="text-sm text-gray-600">All endpoints operational</div>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(healthData?.api_status)}`}>
            {healthData?.api_status || 'Unknown'}
          </span>
        </div>

        {/* Webhooks Status */}
        <div className="border-t pt-4 mt-4">
          <div className="text-sm font-semibold text-gray-700 mb-3">Webhook Services</div>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center">
              <div className={`text-lg mb-1 ${getStatusColor(healthData?.services?.whatsapp_webhook)}`}>
                {getStatusIcon(healthData?.services?.whatsapp_webhook)}
              </div>
              <div className="text-xs text-gray-600">WhatsApp</div>
            </div>
            <div className="text-center">
              <div className={`text-lg mb-1 ${getStatusColor(healthData?.services?.email_webhook)}`}>
                {getStatusIcon(healthData?.services?.email_webhook)}
              </div>
              <div className="text-xs text-gray-600">Email</div>
            </div>
            <div className="text-center">
              <div className={`text-lg mb-1 ${getStatusColor(healthData?.services?.vapi_webhook)}`}>
                {getStatusIcon(healthData?.services?.vapi_webhook)}
              </div>
              <div className="text-xs text-gray-600">Calls</div>
            </div>
          </div>
        </div>

        {/* Error Rates */}
        <div className="border-t pt-4 mt-4">
          <div className="text-sm font-semibold text-gray-700 mb-3">Error Rates</div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {healthData?.error_rates?.last_hour?.toFixed(1) || '0.0'}%
              </div>
              <div className="text-xs text-gray-600">Last Hour</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {healthData?.error_rates?.last_24h?.toFixed(1) || '0.0'}%
              </div>
              <div className="text-xs text-gray-600">Last 24h</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {healthData?.error_rates?.last_7d?.toFixed(1) || '0.0'}%
              </div>
              <div className="text-xs text-gray-600">Last 7 Days</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HealthCheckPanel;

